import type { FC } from 'react';

import { EyeIcon, EyeOffIcon } from '@heroicons/react/solid';
import { useApiClient, useAuth } from '@nodal/api';
import { t } from '@nodal/i18n';
import { ButtonLoading } from '@nodal/uikit/components/ButtonLoading';
import { FormErrors } from '@nodal/uikit/components/FormErrors';
import { TextInput } from '@nodal/uikit/components/TextInput';
import { Form, Formik, type FormikHelpers, useField } from 'formik';
import { useCallback, useState } from 'react';
import * as Yup from 'yup';

import type {
  PasswordsShown,
  SurrogateInvitationFormProps,
  PasswordUpdateFormValues,
} from './SurrogateInvitationForm.interface';

// Custom validation schema for password update
const passwordUpdateValidationSchema = Yup.object().shape({
  password1: Yup.string()
    .required(t('Required field'))
    .min(8, t('Password must contain at least 8 characters')),
  password2: Yup.string()
    .required(t('Required field'))
    .oneOf([Yup.ref('password1')], t('Passwords do not match')),
  agreeToTerms: Yup.boolean().oneOf(
    [true],
    'You must agree to the terms to continue',
  ),
});

// Terms agreement checkbox component
const TermsAgreementCheckbox: FC = () => {
  const [field, meta] = useField('agreeToTerms');

  return (
    <div className="relative">
      <div className="flex items-start">
        <div className="flex items-center h-5">
          <input
            {...field}
            id="agreeToTerms"
            type="checkbox"
            className={`w-5 h-5 rounded text-forest-500 border-gray-300 focus:ring-forest-500 ${
              meta.touched && meta.error
                ? 'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
                : ''
            }`}
          />
        </div>
        <div className="ml-3 text-sm">
          <label htmlFor="agreeToTerms" className="font-medium text-gray-900">
            {t('I agree to the')}{' '}
            <a
              href="https://www.nodal.com/terms-of-use"
              target="_blank"
              rel="noopener noreferrer"
              className="text-forest-500 hover:text-forest-600 underline"
            >
              {t('Nodal Terms')}
            </a>
          </label>
          {meta.touched && meta.error && (
            <div className="mt-1 text-sm text-red-600">{meta.error}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export const SurrogateInvitationForm: FC<SurrogateInvitationFormProps> = ({
  initialValues,
  onSubmit,
  validationSchema,
}) => {
  const [passwordsShown, setPasswordsShown] = useState<PasswordsShown>({
    password1: false,
    password2: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const apiClient = useApiClient();
  const auth = useAuth();

  const handleChangePasswordsShown = useCallback(
    (key: keyof PasswordsShown) =>
      setPasswordsShown({ ...passwordsShown, [key]: !passwordsShown[key] }),
    [passwordsShown],
  );

  const handleAutoSignIn = async (email: string, password: string) => {
    try {
      const response = await apiClient.api.AuthApi.authLoginCreate({
        login: {
          email,
          password,
        },
      });

      if (response.data.access_token && response.data.refresh_token) {
        await auth.tokenProvider.setTokens({
          access: response.data.access_token,
          refresh: response.data.refresh_token,
        });

        window.location.href = '/app';
      }
    } catch (error) {
      window.location.href = '/login';
    }
  };

  const handleSubmit = async (values: PasswordUpdateFormValues) => {
    setIsSubmitting(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    try {
      const verificationKey = window.location.pathname.split('/')[1];

      const response =
        await apiClient.api.RegistrationApi.registrationDonorPasswordUpdate({
          donorPasswordUpdate: {
            verification_key: verificationKey,
            new_password: values.password1,
            confirm_password: values.password2,
          },
        });

      const responseData = response.data as { email?: string };
      const email = responseData?.email;

      if (email) {
        await handleAutoSignIn(email, values.password1);
      } else {
        setSuccessMessage('Your password has been successfully set!');

        if (onSubmit) {
          await onSubmit(values, {} as FormikHelpers<PasswordUpdateFormValues>);
        }
      }
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const errorResponse = error as {
          response?: { data?: Record<string, string[]> };
        };
        const errorData = errorResponse.response?.data;

        if (errorData) {
          if (errorData.verification_key) {
            setErrorMessage(errorData.verification_key.join(', '));
          } else if (errorData.new_password) {
            setErrorMessage(errorData.new_password.join(', '));
          } else if (errorData.confirm_password) {
            setErrorMessage(errorData.confirm_password.join(', '));
          } else {
            setErrorMessage('An error occurred while updating your password.');
          }
        }
      } else {
        setErrorMessage('Network error. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema || passwordUpdateValidationSchema}
      onSubmit={handleSubmit}
    >
      {() => (
        <Form className="space-y-8" noValidate>
          <FormErrors />

          {errorMessage && (
            <div className="p-4 bg-red-50 rounded-md border border-red-200">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-red-800">{errorMessage}</p>
                </div>
              </div>
            </div>
          )}

          {successMessage && (
            <div className="p-4 bg-green-50 rounded-md border border-green-200">
              <div className="flex">
                <div className="shrink-0">
                  <svg
                    className="w-5 h-5 text-green-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-green-800">{successMessage}</p>
                  <div className="mt-2">
                    <a
                      href="/login"
                      className="text-sm font-medium text-green-800 hover:text-green-600 underline"
                    >
                      Click here to log in
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!successMessage && (
            <>
              <div className="relative">
                <TextInput
                  label={t('Password')}
                  name="password1"
                  required
                  type={passwordsShown?.password1 ? 'text' : 'password'}
                />
                <button
                  type="button"
                  onClick={() => handleChangePasswordsShown('password1')}
                  className="absolute right-3 bottom-2 mb-0.25"
                >
                  {passwordsShown?.password1 ? (
                    <EyeOffIcon className="w-5 h-5 text-grey-forest-400" />
                  ) : (
                    <EyeIcon className="w-5 h-5 text-grey-forest-400" />
                  )}
                </button>
              </div>
              <div className="relative">
                <TextInput
                  label={t('Confirm Password')}
                  name="password2"
                  required
                  type={passwordsShown?.password2 ? 'text' : 'password'}
                />
                <button
                  type="button"
                  onClick={() => handleChangePasswordsShown('password2')}
                  className="absolute right-3 bottom-2 mb-0.25"
                >
                  {passwordsShown?.password2 ? (
                    <EyeOffIcon className="w-5 h-5 text-grey-forest-400" />
                  ) : (
                    <EyeIcon className="w-5 h-5 text-grey-forest-400" />
                  )}
                </button>
              </div>

              <TermsAgreementCheckbox />

              <ButtonLoading
                variant="primary"
                type="submit"
                className="justify-center w-full"
                loading={isSubmitting}
              >
                Complete
              </ButtonLoading>
            </>
          )}
        </Form>
      )}
    </Formik>
  );
};
